// File: app/src/components/workout/WorkoutFeedback.js
//
// Inline feedback section shown on the completion screen and before early saves.
// Collects RPE (1-10) and optional session notes.

import React, { useState } from "react";
import HelpLink from "../help/HelpLink";
import { Button } from "../ui/button";
import EffortMeter from "./EffortMeter";

// Controlled RPE selector + notes textarea. Shared by the completion screen
// (via WorkoutFeedback) and the inline "Stop workout?" dialog so the effort
// scale is defined in exactly one place.
// `sideBySide`: effort and notes in two columns, for a short screen such as a
// phone on its side (#227).
export const FeedbackFields = ({ rpe, onRpeChange, notes, onNotesChange, sideBySide = false }) => (
  <div className={sideBySide ? "grid w-full grid-cols-2 gap-4" : "w-full flex flex-col gap-4"}>
    <EffortMeter
      value={rpe}
      onChange={onRpeChange}
      compact={sideBySide}
      label={
        <>
          Perceived effort
          <HelpLink topic="effort#the-scale" label="What do the numbers mean?" />
        </>
      }
    />

    {/* Session notes */}
    <div className={sideBySide ? "flex" : undefined}>
      <textarea
        value={notes}
        onChange={(e) => onNotesChange(e.target.value)}
        placeholder="How did this session feel? (optional)"
        rows={2}
        className="w-full flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/25 resize-none"
      />
    </div>
  </div>
);

const WorkoutFeedback = ({
  onSave,
  onSkip,
  saveLabel = "Save",
  skipLabel = "Skip",
  // Optional third way out (#199): Keep going, which carries the session on
  // instead of saving it. { label, hint?, icon?, onClick(feedback) }
  alsoAction = null,
}) => {
  const [rpe, setRpe] = useState(null);
  const [notes, setNotes] = useState("");
  // One-shot: the modal unmounts once the handler completes, but a double-tap
  // lands both presses before that — replaying side effects (the victory
  // fanfare) even though the save itself is deduped downstream (issue #4).
  const [submitted, setSubmitted] = useState(false);

  const submitOnce = (fn) => {
    if (submitted) return;
    setSubmitted(true);
    fn();
  };

  return (
    <div className="w-full flex flex-col gap-4">
      <FeedbackFields
        rpe={rpe}
        onRpeChange={setRpe}
        notes={notes}
        onNotesChange={setNotes}
      />

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          onClick={() =>
            submitOnce(() => onSave({ rpe, notes: notes.trim() || undefined }))
          }
          disabled={submitted}
          className="flex-1"
          size="lg"
        >
          {saveLabel}
        </Button>
        <Button
          onClick={() => submitOnce(() => onSkip())}
          disabled={submitted}
          variant="ghost"
          size="lg"
          className="text-muted-foreground"
        >
          {skipLabel}
        </Button>
      </div>
      {alsoAction && (
        <Button
          onClick={() =>
            submitOnce(async () => {
              // false: it didn't happen (a live session that wouldn't reopen),
              // so the buttons come back.
              const done = await alsoAction.onClick({ rpe, notes: notes.trim() || undefined });
              if (done === false) setSubmitted(false);
            })
          }
          disabled={submitted}
          variant="outline"
          size="lg"
          className="h-auto w-full flex-col gap-0.5 py-2.5"
        >
          <span className="flex items-center gap-2">
            {alsoAction.icon && <alsoAction.icon className="h-4 w-4" />}
            {alsoAction.label}
          </span>
          {alsoAction.hint && (
            <span className="text-xs font-normal text-muted-foreground">{alsoAction.hint}</span>
          )}
        </Button>
      )}
    </div>
  );
};

export default WorkoutFeedback;
